import React, { useState, useEffect, useCallback } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { draftId, LISTING_STATE_PUBLISHED, PRIME_SEARCH, propTypes, SUB_SEARCH, userTypeSub } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  AlgoliaLocationSearch,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { createSlug, LISTING_PAGE_PARAM_TYPE_DRAFT, LISTING_PAGE_PARAM_TYPE_EDIT, parse } from '../../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';



const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.navigationLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.navigationLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders', type: 'all' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentUserHasListings, currentPage, currentUser, isCurrentUserProfessional, onLogout, listingId, state, title, isCurrentUserClient, notificationCount }) => {
  const editListingLinkType = state == "draft"
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const id = currentUser && currentUser.id && currentUser.id.uuid
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      {currentUserHasListings ?
        <MenuContent className={css.profileWrapper}>
          {
            isCurrentUserProfessional && state == LISTING_STATE_PUBLISHED ?

              <MenuItem key="ProfilePage">
                <NamedLink
                  className={classNames(css.menuLink, currentPageClass('ProfilePage'))}
                  name="ProfilePage"
                  params={{ id }}
                >
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarDesktop.profileLink" />
                </NamedLink>
              </MenuItem>
              : isCurrentUserProfessional ?
                <MenuItem key="EditListingPage">
                  <NamedLink
                    className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
                    name="EditListingPage"
                    params={{ slug: title, id: listingId, type: editListingLinkType, tab: 'details' }}
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
                  </NamedLink>
                </MenuItem>
                :
                <MenuItem key="ProfileSettingsPage">
                  <NamedLink
                    className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
                    name="ProfileSettingsPage"
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarDesktop.listingprofileSettingsLink" />
                  </NamedLink>
                </MenuItem>
          }
          <MenuItem key="InvitationsPage" isDisabled={isCurrentUserClient}>
            <NamedLink
              className={classNames(css.menuLink, currentPageClass("InvitationsPage"))}
              name="InvitationsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.invitations" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="CommunityPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('CommunityPage'))}
              name="CommunityPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.CommunityPage" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
        :
        <MenuContent className={css.profileWrapper}>
          {
            isCurrentUserProfessional && listingId ?
              <MenuItem key="EditListingPage">
                <NamedLink
                  className={classNames(css.menuLink, currentPageClass('EditListIn the invitation flow, Prime invites a sub for their listing. This is not a transaction; it only sends an email and displays all invited listings on the invitation page. The sub can then place a bid or make an inquiry, which will be reflected on the dashboard.ingPage'))}
                  name="EditListingPage"
                  params={{ slug: title, id: listingId, type: editListingLinkType, tab: 'details' }}
                >
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
                </NamedLink>
              </MenuItem>
              : isCurrentUserProfessional ?
                <MenuItem key="EditListingPage">
                  <NamedLink
                    className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
                    name="EditListingPage"
                    params={{ slug: 'draft', id: draftId, type: 'new', tab: 'details' }}
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
                  </NamedLink>
                </MenuItem> :
                <MenuItem key="ProfileSettingsPage">
                  <NamedLink
                    className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
                    name="ProfileSettingsPage"
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
                  </NamedLink>
                </MenuItem>
          }
          <MenuItem key="CommunityPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('CommunityPage'))}
              name="CommunityPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.CommunityPage" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      }
    </Menu>
  );
};



const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    location,
    history,
    isCurrentUserProfessional,
    isCurrentUserClient,
    currentUserLocationInfo,
    initialSearchFormValues,
  } = props;
  const routesConfiguration = useRouteConfiguration();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const { clientType, listingId, state, title="title", userType } = (currentUser && currentUser.attributes && currentUser.attributes.profile.publicData) || {};
  const { search } = location || null;
  const currentUserHasListings = state == "published"? true:false
  const showListing = userType == userTypeSub ? userType == userTypeSub : location.pathname.includes(PRIME_SEARCH)
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const handleRefineLocation = location => {
    if (location && location.address && location.bounds) {
      const updatedParams = { ...queryParams, ...location };
      history.push(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          { tab: showListing ? PRIME_SEARCH : SUB_SEARCH },
          updatedParams
        )
      );
    }
  };

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      currentUserHasListings={currentUserHasListings}
      isCurrentUserProfessional={isCurrentUserProfessional}
      isCurrentUserClient={isCurrentUserClient}
      listingId={listingId}
      state={state}
      title={title}
      notificationCount={notificationCount} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const demoLink = !isAuthenticated ? (

    <NamedLink className={css.navigationButton} name="SignupPage">
      <FormattedMessage id="TopbarDesktop.demoBook" />
    </NamedLink>

  ) : null;


  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isProjectManagementPage =
      page === 'ManagementPage' && PROJECT_MANAGEMENT_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage
      ? css.currentPage
      : null || isProjectManagementPage
        ? css.currentPage
        : null;
  };

  const howWeWorkMenu = (
    <Menu rootClassName={css.navigationMenu}>
      <MenuLabel
        className={classNames(css.navigationLinkText)}
        isOpenClassName={css.navigationMenuIsOpen}
      >
        <FormattedMessage id="TopbarDesktop.howWeWorkMenu" />
        {/* <IconArrowHead className={css.avatarArrow} direction="down" /> */}
      </MenuLabel>
      <MenuContent className={css.navigationMenuContent}>
        <MenuItem key="lookingForProjects">
          <NamedLink
            className={classNames(css.companymenuLink, currentPageClass('SearchPage'))}
            name="SearchPage"
            params={{ tab: 'projects' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.lookingForProjects" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="LookingForSubContractors">
          <NamedLink
            className={classNames(css.companymenuLink, currentPageClass('ProsSearchPage'))}
            name="SearchPage"
            params={{ tab: 'pros' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.lookingForSubContractors" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  const company = (
    <Menu rootClassName={css.navigationMenu}>
      <MenuLabel
        className={classNames(css.navigationLinkText)}
        isOpenClassName={css.navigationMenuIsOpen}
      >
        <FormattedMessage id="TopbarDesktop.company" />
        {/* <IconArrowHead className={css.avatarArrow} direction="down" /> */}
      </MenuLabel>
      <MenuContent className={css.navigationMenuContent}>
        <MenuItem key="aboutPage">
          <a
            className={css.menuLink}
            href='/p/about'
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="SectionFooter.about" />
          </a>
        </MenuItem>

        <MenuItem key="lookingForProjects">
          <a
            className={css.menuLink}
            href='/p/subscription'
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.membership" />
          </a>
        </MenuItem>
        <MenuItem key="LookingForSubContractor">
          <a
            className={css.menuLink}
            href='/p/support'
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="SectionFooter.support" />
          </a>
        </MenuItem>
        <MenuItem key="ContarctPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('Contactpage'))}
            name="ContactPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="SectionFooter.contact" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  const createProfile = isAuthenticated && clientType == "undefined" ? (
    <NamedLink
      className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
      name="ProfileSettingsPage"

    >
      <FormattedMessage id="TopbarDesktop.createProfessionalPage" />
    </NamedLink>
  ) : null;

  const findProjectsLink = isCurrentUserClient ? (
    <NamedLink
      className={classNames(css.navigationLinkText, {
        [css.navigationLinkActive]: currentPageClass('SearchPage'),
      })}
      name="SearchPage"
      params={{ tab: 'pros' }}
    >
      <FormattedMessage id="TopbarDesktop.findPros" />
    </NamedLink>
  ) : (<NamedLink
    className={classNames(css.navigationLinkText, {
      [css.navigationLinkActive]: currentPageClass('SearchPage'),
    })}
    name="SearchPage"
    params={{ tab: 'projects' }}
  >
    <FormattedMessage id={"TopbarDesktop.findProjects"} />
  </NamedLink>);

  const editListingLinkType = state == "draft"
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const subCreateProfile = (isAuthenticated && isCurrentUserProfessional && !listingId) ? (
    <NamedLink
      className={classNames(css.navigationLinkText, currentPageClass('EditListingPage'))}
      name="EditListingPage"
      params={{ slug: 'draft', id: draftId, type: 'new', tab: 'details' }}
    >
      <span className={css.menuItemBorder} />
      <FormattedMessage id="TopbarDesktop.createProfessionalPage" />
    </NamedLink>
  ) : (
    isAuthenticated && isCurrentUserProfessional && listingId && state != LISTING_STATE_PUBLISHED ?
      <NamedLink
        className={classNames(css.navigationLinkText, currentPageClass('EditListingPage'))}
        name="EditListingPage"
        params={{ slug: title, id: listingId, type: editListingLinkType, tab: 'details' }}

      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.createProfessionalPage" />
      </NamedLink>
      : null);



  return (
    <nav className={classes}>
      <div className={css.topbarWrapper}>
        <div className={css.logoAndSearchContainer}>
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          {/* <TopbarSearchForm
            className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
            desktopInputRoot={css.topbarSearchWithLeftPadding}
            onSubmit={onSearchSubmit}
            initialValues={initialSearchFormValues}
            appConfig={config}
          /> */}
          <AlgoliaLocationSearch
            attribute={'publicData.location.address'}
            defaultLocation={currentUserLocationInfo}
            onRefineLocation={useCallback(l => handleRefineLocation(l), [
              queryParams,
            ])}
          />
        </div>
        {createProfile}

        <div className={css.navigation}>
          {company}
          <NamedLink name="CommunityPage" className={isAuthenticated ? css.navigationLinkText : css.navigationLinkTexts}>
            <FormattedMessage id="TopbarDesktop.CommunityPage" />
          </NamedLink>
          {state == LISTING_STATE_PUBLISHED ? null : subCreateProfile}
          {isAuthenticated && clientType != "undefined" && findProjectsLink}
          {isAuthenticated && isCurrentUserClient &&
            <div className={css.customMenu}>
              {isAuthenticated && isCurrentUserClient && !currentUserHasListings ? (
                <CustomLinksMenu
                  currentPage={currentPage}
                  customLinks={customLinks}
                  intl={intl}
                  hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
                />
              ) : (
                isAuthenticated && isCurrentUserClient && currentUserHasListings && (
                  <NamedLink
                    className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
                    name="ManageListingsPage"
                  >
                    <FormattedMessage id="TopbarDesktop.PrimeProfilePage" />
                  </NamedLink>
                )
              )}
            </div>
          }

          {(isAuthenticated && currentUserHasListings) ?
            <NamedLink
              className={css.navigationLinkTextBox}
              name="InboxPage"
              params={{ tab: isCurrentUserClient ? 'sales' : 'orders', type: 'all' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.Dashboard" />
              {notificationCount > 0 ? <span className={css.notificationCounts}></span> : null}

            </NamedLink>
            : null}

          {profileMenuMaybe}
          {!isAuthenticated && howWeWorkMenu}

          {signupLinkMaybe}
          {loginLinkMaybe}
          {demoLink}
        </div>
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
